import type { AxiosRequestConfig, Method } from 'axios';
import { ApiCallMethod } from '@cabal/shared/api-client/types';

/**
 * Per-call opt-out header for the response interceptor's auto-retry. A request
 * carrying this header rejects on its FIRST failure instead of being retried
 * with exponential backoff. Set it at the call site for non-idempotent,
 * user-facing mutations — e.g. every mutation fired from the Time Machine modal
 * (travel / halt / rename) — where ~7s of silent backoff would keep the UI in a
 * fake "loading" state and surface the error late. The transport strips it from
 * the outgoing request and translates it to the private `_noRetry` config flag
 * config, so it never reaches the wire; only the local
 * interceptor reads it. Every other request's retry behavior is untouched.
 */
export const NO_RETRY_HEADER = 'X-No-Retry';

interface PreparedTransportHeaders {
  headers: Record<string, string>;
  noRetry: boolean;
}

interface TransportRequestConfigInput<B, Q extends object | undefined> {
  method: ApiCallMethod;
  url: string;
  headers: Record<string, string>;
  query: Q;
  body: B;
  signal?: AbortSignal;
}

interface TransportRequestConfigLocal extends AxiosRequestConfig {
  _noRetry?: boolean;
}

const AXIOS_METHOD_BY_API_METHOD: Record<ApiCallMethod, Method> = {
  [ApiCallMethod.Get]: 'get',
  [ApiCallMethod.Post]: 'post',
  [ApiCallMethod.Put]: 'put',
  [ApiCallMethod.Patch]: 'patch',
  [ApiCallMethod.Delete]: 'delete',
};

function prepareTransportHeaders(input: {
  headers: Record<string, string>;
  body: unknown;
}): PreparedTransportHeaders {
  const headers = { ...input.headers };
  const noRetry = NO_RETRY_HEADER in headers;
  if (noRetry) {
    delete headers[NO_RETRY_HEADER];
  }

  const isFormData =
    typeof FormData !== 'undefined' && input.body instanceof FormData;
  if (isFormData) {
    delete headers['Content-Type'];
  }

  return { headers, noRetry };
}

export function createTransportRequestConfig<
  B,
  Q extends object | undefined = undefined,
>(
  input: TransportRequestConfigInput<B, Q>,
): TransportRequestConfigLocal {
  const preparedHeaders = prepareTransportHeaders({
    headers: input.headers,
    body: input.body,
  });
  const requestConfig: TransportRequestConfigLocal = {
    method: AXIOS_METHOD_BY_API_METHOD[input.method],
    url: input.url,
    params: input.query as AxiosRequestConfig['params'],
    data: input.body,
    headers: preparedHeaders.headers,
    signal: input.signal,
  };

  if (preparedHeaders.noRetry) {
    requestConfig._noRetry = true;
  }

  return requestConfig;
}
